import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

import { DemoDataService } from './demo-data.service';
import { LeaseService } from './lease.service';

// ---- Payment methods (what guests/tenants can pay with) ----
export type PaymentMethodKey = 'ach' | 'card' | 'wire';
export type PaymentMethodLabel = 'ACH' | 'Credit Card' | 'Wire';

export interface PaymentMethodOption {
  key: PaymentMethodKey;
  label: string;
  rate: string;
  description: string;
  enabled: boolean;
  fee_paid_by: 'tenant' | 'owner';
}

// ---- Payout account (where TIDY deposits the money it collects) ----
export interface PayoutAccount {
  connected: boolean;
  provider: string;
  account_name: string;
  last4: string;
  schedule: 'Daily' | 'Weekly' | 'Monthly';
}

// ---- Payment reminder cadence + branding ----
export interface ReminderRule {
  id: string;
  offset_days: number; // negative = before due, 0 = on due date, positive = after
  label: string;
  enabled: boolean;
  message: string;
}

export interface ReminderSettings {
  logo_url: string;
  accent_color: string;
  heading: string;
  message: string;
  rules: ReminderRule[];
}

// ---- A payment (receivable): one record, two lifecycle stages ----
// It starts life *owed* (Due/Overdue, no method yet) and ends *collected* (Paid).
export type PaymentStatus = 'Due' | 'Overdue' | 'Paid' | 'Failed';

export interface PaymentEvent {
  label: string;
  date: string;
  done: boolean;
}

export interface Payment {
  id: string;            // UUID — the slug for the standalone pay link
  payer_name: string;
  payer_type: 'Tenant' | 'Guest';
  property_name: string;
  unit: string;
  description: string;   // "June rent", "Security deposit"…
  amount: number;        // base amount owed
  due_date: string;      // ISO
  status: PaymentStatus;
  source: 'Recurring' | 'Manual';
  // filled in once paid:
  method: PaymentMethodLabel | null;
  fee: number;
  fee_paid_by: 'tenant' | 'owner';
  paid_date: string | null;
  payout_status: 'Paid out' | 'In transit' | 'Scheduled' | null;
  timeline: PaymentEvent[];

  // Room-splitting — optional, undefined means "whole unit, monthly" (unchanged behavior)
  space_name?: string;
  cadence?: 'weekly' | 'monthly';

  // A payment is always credited against a reservation. For tenants, that reservation
  // is the lease — `lease_id` links back to LeaseService's Lease.id so the payments UI
  // can deep-link to the lease ledger. Guest payments would carry an equivalent
  // booking/reservation reference instead, but no guest booking/reservation id exists
  // anywhere in this dummy data yet, so there's no symmetric field to add here today.
  lease_id?: number;
}

const PAYMENT_METHODS: PaymentMethodOption[] = [
  {
    key: 'ach',
    label: 'ACH bank transfer',
    rate: '0.8% (max $5)',
    description: 'Lowest cost. 3 business-day settlement. Best for recurring rent.',
    enabled: true,
    fee_paid_by: 'tenant',
  },
  {
    key: 'card',
    label: 'Credit / debit card',
    rate: '2.9% + 30¢',
    description: 'Instant, highest convenience. Higher processing rate.',
    enabled: true,
    fee_paid_by: 'tenant',
  },
  {
    key: 'wire',
    label: 'Wire transfer',
    rate: '$15 flat',
    description: 'For large one-off payments (deposits, move-in).',
    enabled: false,
    fee_paid_by: 'tenant',
  },
];

const PAYOUT_ACCOUNT: PayoutAccount = {
  connected: false,
  provider: 'Mercury',
  account_name: 'Rent Operating Account',
  last4: '4837',
  schedule: 'Daily',
};

function ruleMessage(when: string): string {
  return `Hi {tenant}, this is a reminder that your {amount} payment for {property} is ${when}. Tap the secure link to pay in seconds: {link}`;
}

const REMINDER_SETTINGS: ReminderSettings = {
  logo_url: '',
  accent_color: '#1a8e9c',
  heading: 'Your payment is ready',
  message: 'Pay securely with ACH, card, or wire. Your information is encrypted and never shared.',
  rules: [
    { id: 'r-3', offset_days: -3, label: '3 days before due', enabled: true, message: ruleMessage('due in 3 days') },
    { id: 'r0', offset_days: 0, label: 'On the due date', enabled: true, message: ruleMessage('due today') },
    { id: 'r3', offset_days: 3, label: '3 days after due', enabled: true, message: ruleMessage('now 3 days past due') },
    { id: 'r7', offset_days: 7, label: '7 days after due', enabled: true, message: ruleMessage('now a week past due') },
    { id: 'r14', offset_days: 14, label: '14 days after due', enabled: true, message: ruleMessage('seriously overdue — please pay to avoid late fees') },
    { id: 'r30', offset_days: 30, label: '30 days after due', enabled: false, message: ruleMessage('30 days past due') },
  ],
};

function paidTimeline(created: string, sent: string, paid: string): PaymentEvent[] {
  return [
    { label: 'Payment request created', date: created, done: true },
    { label: 'Sent to payer', date: sent, done: true },
    { label: 'Opened by payer', date: sent, done: true },
    { label: 'Paid', date: paid, done: true },
  ];
}

function dueTimeline(created: string, sent: string, opened: boolean): PaymentEvent[] {
  return [
    { label: 'Payment request created', date: created, done: true },
    { label: 'Sent to payer', date: sent, done: true },
    { label: 'Opened by payer', date: opened ? sent : '', done: opened },
    { label: 'Paid', date: '', done: false },
  ];
}

const DUMMY_PAYMENTS: Payment[] = [
  // --- Due (upcoming) ---
  {
    id: 'a1c4e7b2-0014-4a10-9f01-001122334455',
    payer_name: 'Priya Raman', payer_type: 'Tenant',
    property_name: 'Oakwood Apartments', unit: 'Apt 3B',
    description: 'August rent', amount: 1850, due_date: '2026-08-01', status: 'Paid', source: 'Recurring',
    method: 'ACH', fee: 5, fee_paid_by: 'tenant', paid_date: '2026-08-01', payout_status: 'In transit',
    timeline: paidTimeline('2026-07-25', '2026-07-28', '2026-08-01'),
  },
  {
    id: 'a1c4e7b2-0015-4a10-9f01-001122334455',
    payer_name: 'The Alvarez Family', payer_type: 'Tenant',
    property_name: 'Cedar Court', unit: 'Unit 7',
    description: 'August rent', amount: 2600, due_date: '2026-08-01', status: 'Due', source: 'Recurring',
    method: null, fee: 0, fee_paid_by: 'tenant', paid_date: null, payout_status: null,
    timeline: dueTimeline('2026-07-25', '2026-07-28', false),
  },
  // --- Overdue ---
  {
    id: 'a1c4e7b2-0003-4a10-9f01-001122334455',
    payer_name: 'Marcus T.', payer_type: 'Guest',
    property_name: 'Mountain Cabin', unit: 'Whole home',
    description: 'Cleaning + damage deposit', amount: 600, due_date: '2026-07-04', status: 'Overdue', source: 'Manual',
    method: null, fee: 0, fee_paid_by: 'tenant', paid_date: null, payout_status: null,
    timeline: dueTimeline('2026-06-28', '2026-06-28', false),
  },
  {
    id: 'a1c4e7b2-0004-4a10-9f01-001122334455',
    payer_name: 'Aaron Cole', payer_type: 'Tenant',
    property_name: 'Maple Street Duplex', unit: 'Unit A',
    description: 'June rent', amount: 2400, due_date: '2026-06-01', status: 'Overdue', source: 'Recurring',
    method: null, fee: 0, fee_paid_by: 'tenant', paid_date: null, payout_status: null,
    timeline: dueTimeline('2026-05-25', '2026-05-28', true),
  },
  {
    id: 'a1c4e7b2-0005-4a10-9f01-001122334455',
    payer_name: 'Grace Kim', payer_type: 'Tenant',
    property_name: 'Downtown Loft', unit: 'Unit 5',
    description: 'June rent', amount: 2100, due_date: '2026-06-15', status: 'Overdue', source: 'Recurring',
    method: null, fee: 0, fee_paid_by: 'tenant', paid_date: null, payout_status: null,
    timeline: dueTimeline('2026-06-08', '2026-06-11', true),
  },
  // --- Failed (needs retry) ---
  {
    id: 'a1c4e7b2-0006-4a10-9f01-001122334455',
    payer_name: 'Sofia Martinez', payer_type: 'Tenant',
    property_name: 'Riverside Townhomes', unit: 'Unit 4',
    description: 'June rent', amount: 2950, due_date: '2026-06-01', status: 'Failed', source: 'Recurring',
    method: 'ACH', fee: 5, fee_paid_by: 'tenant', paid_date: null, payout_status: null,
    timeline: [
      { label: 'Payment request created', date: '2026-05-25', done: true },
      { label: 'Sent to payer', date: '2026-05-28', done: true },
      { label: 'ACH payment attempted', date: '2026-06-02', done: true },
      { label: 'Failed — insufficient funds (R01)', date: '2026-06-04', done: true },
    ],
  },
  // --- Paid ---
  {
    id: 'a1c4e7b2-0001-4a10-9f01-001122334455',
    payer_name: 'Priya Raman', payer_type: 'Tenant',
    property_name: 'Oakwood Apartments', unit: 'Apt 3B',
    description: 'July rent', amount: 1850, due_date: '2026-07-01', status: 'Paid', source: 'Recurring',
    method: 'ACH', fee: 5, fee_paid_by: 'tenant', paid_date: '2026-07-02', payout_status: 'Paid out',
    timeline: paidTimeline('2026-06-24', '2026-06-27', '2026-07-02'),
  },
  {
    id: 'a1c4e7b2-0002-4a10-9f01-001122334455',
    payer_name: 'The Alvarez Family', payer_type: 'Tenant',
    property_name: 'Cedar Court', unit: 'Unit 7',
    description: 'July rent', amount: 2600, due_date: '2026-07-01', status: 'Paid', source: 'Recurring',
    method: 'ACH', fee: 5, fee_paid_by: 'tenant', paid_date: '2026-07-03', payout_status: 'Paid out',
    timeline: paidTimeline('2026-06-24', '2026-06-27', '2026-07-03'),
  },
  {
    id: 'a1c4e7b2-0007-4a10-9f01-001122334455',
    payer_name: 'Diego Alvarez', payer_type: 'Tenant',
    property_name: 'Riverside Townhomes', unit: 'Unit 9',
    description: 'June rent', amount: 2950, due_date: '2026-06-01', status: 'Paid', source: 'Recurring',
    method: 'Credit Card', fee: 85.85, fee_paid_by: 'tenant', paid_date: '2026-06-02', payout_status: 'Paid out',
    timeline: paidTimeline('2026-05-25', '2026-05-28', '2026-06-02'),
  },
  {
    id: 'a1c4e7b2-0008-4a10-9f01-001122334455',
    payer_name: 'Emily R.', payer_type: 'Guest',
    property_name: 'Beach House', unit: 'Whole home',
    description: 'Weekend stay balance', amount: 1240, due_date: '2026-06-03', status: 'Paid', source: 'Manual',
    method: 'Credit Card', fee: 36.26, fee_paid_by: 'tenant', paid_date: '2026-06-03', payout_status: 'Paid out',
    timeline: paidTimeline('2026-05-30', '2026-05-30', '2026-06-03'),
  },
  {
    id: 'a1c4e7b2-0009-4a10-9f01-001122334455',
    payer_name: 'Priya Raman', payer_type: 'Tenant',
    property_name: 'Oakwood Apartments', unit: 'Apt 3B',
    description: 'June rent', amount: 1850, due_date: '2026-06-01', status: 'Paid', source: 'Recurring',
    method: 'ACH', fee: 5, fee_paid_by: 'tenant', paid_date: '2026-06-01', payout_status: 'Paid out',
    timeline: paidTimeline('2026-05-25', '2026-05-28', '2026-06-01'),
  },
  {
    id: 'a1c4e7b2-0010-4a10-9f01-001122334455',
    payer_name: 'The Alvarez Family', payer_type: 'Tenant',
    property_name: 'Cedar Court', unit: 'Unit 7',
    description: 'June rent', amount: 2600, due_date: '2026-06-01', status: 'Paid', source: 'Recurring',
    method: 'ACH', fee: 5, fee_paid_by: 'tenant', paid_date: '2026-06-01', payout_status: 'Paid out',
    timeline: paidTimeline('2026-05-25', '2026-05-28', '2026-06-01'),
  },
  // --- Room-splitting demo: weekly recurring room rent (Palmetto House, Room A) ---
  {
    id: 'a1c4e7b2-0019-4a10-9f01-001122334455',
    payer_name: 'Marcus Whitfield', payer_type: 'Tenant',
    property_name: 'Palmetto House', unit: 'Room A',
    description: 'Weekly rent · wk of Aug 3', amount: 225, due_date: '2026-08-03', status: 'Due', source: 'Recurring',
    method: null, fee: 0, fee_paid_by: 'tenant', paid_date: null, payout_status: null,
    space_name: 'Room A', cadence: 'weekly', lease_id: 5,
    timeline: dueTimeline('2026-07-27', '2026-07-28', false),
  },
  {
    id: 'a1c4e7b2-0018-4a10-9f01-001122334455',
    payer_name: 'Marcus Whitfield', payer_type: 'Tenant',
    property_name: 'Palmetto House', unit: 'Room A',
    description: 'Weekly rent · wk of Jul 27', amount: 225, due_date: '2026-07-27', status: 'Overdue', source: 'Recurring',
    method: null, fee: 0, fee_paid_by: 'tenant', paid_date: null, payout_status: null,
    space_name: 'Room A', cadence: 'weekly', lease_id: 5,
    timeline: dueTimeline('2026-07-20', '2026-07-21', true),
  },
  {
    id: 'a1c4e7b2-0017-4a10-9f01-001122334455',
    payer_name: 'Marcus Whitfield', payer_type: 'Tenant',
    property_name: 'Palmetto House', unit: 'Room A',
    description: 'Weekly rent · wk of Jul 20', amount: 225, due_date: '2026-07-20', status: 'Paid', source: 'Recurring',
    method: 'Credit Card', fee: 6.83, fee_paid_by: 'tenant', paid_date: '2026-07-20', payout_status: 'Paid out',
    space_name: 'Room A', cadence: 'weekly', lease_id: 5,
    timeline: paidTimeline('2026-07-13', '2026-07-14', '2026-07-20'),
  },
  {
    id: 'a1c4e7b2-0016-4a10-9f01-001122334455',
    payer_name: 'Marcus Whitfield', payer_type: 'Tenant',
    property_name: 'Palmetto House', unit: 'Room A',
    description: 'Weekly rent · wk of Jul 13', amount: 225, due_date: '2026-07-13', status: 'Paid', source: 'Recurring',
    method: 'Credit Card', fee: 6.83, fee_paid_by: 'tenant', paid_date: '2026-07-13', payout_status: 'Paid out',
    space_name: 'Room A', cadence: 'weekly', lease_id: 5,
    timeline: paidTimeline('2026-07-06', '2026-07-07', '2026-07-13'),
  },
  {
    id: 'a1c4e7b2-0011-4a10-9f01-001122334455',
    payer_name: 'Marcus Whitfield', payer_type: 'Tenant',
    property_name: 'Palmetto House', unit: 'Room A',
    description: 'Weekly rent · wk of Jul 6', amount: 225, due_date: '2026-07-06', status: 'Paid', source: 'Recurring',
    method: 'Credit Card', fee: 6.83, fee_paid_by: 'tenant', paid_date: '2026-07-08', payout_status: 'Paid out',
    space_name: 'Room A', cadence: 'weekly', lease_id: 5,
    timeline: paidTimeline('2026-06-29', '2026-06-30', '2026-07-08'),
  },
  {
    id: 'a1c4e7b2-0012-4a10-9f01-001122334455',
    payer_name: 'Marcus Whitfield', payer_type: 'Tenant',
    property_name: 'Palmetto House', unit: 'Room A',
    description: 'Weekly rent · wk of Jun 29', amount: 225, due_date: '2026-06-29', status: 'Paid', source: 'Recurring',
    method: 'Credit Card', fee: 6.83, fee_paid_by: 'tenant', paid_date: '2026-06-29', payout_status: 'Paid out',
    space_name: 'Room A', cadence: 'weekly', lease_id: 5,
    timeline: paidTimeline('2026-06-22', '2026-06-23', '2026-06-29'),
  },
  // --- Room-splitting demo: overdue room rent (Ferncreek Rooming House, Room 2) ---
  {
    id: 'a1c4e7b2-0013-4a10-9f01-001122334455',
    payer_name: 'Dwayne Pruitt', payer_type: 'Tenant',
    property_name: 'Ferncreek Rooming House', unit: 'Room 2',
    description: 'June rent', amount: 675, due_date: '2026-06-01', status: 'Overdue', source: 'Recurring',
    method: null, fee: 0, fee_paid_by: 'tenant', paid_date: null, payout_status: null,
    space_name: 'Room 2', cadence: 'monthly', lease_id: 9,
    timeline: dueTimeline('2026-05-25', '2026-05-28', true),
  },
];

@Injectable({
  providedIn: 'root'
})
export class RentCollectionService {

  private http = inject(HttpClient);
  private demoData = inject(DemoDataService);
  private leaseService = inject(LeaseService);
  private useDummyData = true;

  // Rent collection isn't live yet, so a real account has no receivables — showing the
  // demo world's payers there would be fabricating their data. The demo account is the
  // only place the sample pipeline belongs. (Methods/payout/reminder settings stay
  // visible everywhere: they're generic product config, not account data.)
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  // ---- Payments (receivables) ----
  async getPayments(): Promise<Payment[]> {
    if (this.useDummyData) { return this.showsDummyData ? DUMMY_PAYMENTS : []; }
    return firstValueFrom(this.http.get<Payment[]>(`${environment.api_url}api/v1/customer/payments`));
  }

  async getPayment(id: string): Promise<Payment | undefined> {
    if (this.useDummyData) { return this.showsDummyData ? DUMMY_PAYMENTS.find(p => p.id === id) : undefined; }
    return firstValueFrom(this.http.get<Payment>(`${environment.api_url}api/v1/customer/payments/${id}`));
  }

  // Owner manually requests a payment from a tenant/guest.
  async createPaymentRequest(input: {
    payer_name: string;
    payer_type: 'Tenant' | 'Guest';
    property_name: string;
    unit: string;
    description: string;
    amount: number;
    due_date: string;
  }): Promise<Payment> {
    const today = new Date().toISOString().slice(0, 10);
    const payment: Payment = {
      id: this.newId(),
      payer_name: input.payer_name,
      payer_type: input.payer_type,
      property_name: input.property_name,
      unit: input.unit,
      description: input.description,
      amount: input.amount,
      due_date: input.due_date,
      status: 'Due',
      source: 'Manual',
      method: null,
      fee: 0,
      fee_paid_by: 'tenant',
      paid_date: null,
      payout_status: null,
      timeline: [
        { label: 'Payment request created', date: today, done: true },
        { label: 'Sent to payer', date: '', done: false },
        { label: 'Opened by payer', date: '', done: false },
        { label: 'Paid', date: '', done: false },
      ],
    };
    DUMMY_PAYMENTS.unshift(payment);
    return payment;
  }

  // Tenant pays via the hosted page (mock) — flips the record to Paid.
  async markPaid(id: string, methodKey: PaymentMethodKey): Promise<Payment | undefined> {
    const payment = DUMMY_PAYMENTS.find(p => p.id === id);
    if (!payment) { return undefined; }
    const method = PAYMENT_METHODS.find(m => m.key === methodKey);
    const today = new Date().toISOString().slice(0, 10);
    payment.status = 'Paid';
    payment.method = (method ? this.methodLabel(method.key) : 'ACH');
    payment.fee = this.estimateFee(payment.amount, methodKey);
    payment.fee_paid_by = method?.fee_paid_by || 'tenant';
    payment.paid_date = today;
    payment.payout_status = 'Scheduled';
    payment.timeline = paidTimeline(
      payment.timeline[0]?.date || today,
      payment.timeline[1]?.date || today,
      today,
    );
    await this.creditLeaseLedger(payment, `${payment.description} · ${payment.method}`);
    return payment;
  }

  // Owner records a payment received outside TIDY (cash, check, external transfer).
  async recordOfflinePayment(id: string): Promise<Payment | undefined> {
    const payment = DUMMY_PAYMENTS.find(p => p.id === id);
    if (!payment) { return undefined; }
    const today = new Date().toISOString().slice(0, 10);
    payment.status = 'Paid';
    payment.method = null;
    payment.fee = 0;
    payment.paid_date = today;
    payment.payout_status = 'Paid out';
    payment.timeline = [
      { label: 'Payment request created', date: payment.timeline[0]?.date || today, done: true },
      { label: 'Marked paid outside TIDY (cash / check)', date: today, done: true },
    ];
    await this.creditLeaseLedger(payment, `${payment.description} · paid outside TIDY`);
    return payment;
  }

  // A tenant payment is credited against its reservation — the lease — so
  // paying here must also land in the linked lease ledger; otherwise the
  // payments UI shows Paid while the ledger still shows the rent as owed.
  private async creditLeaseLedger(payment: Payment, description: string): Promise<void> {
    if (payment.lease_id === undefined) { return; }
    await this.leaseService.recordPayment(payment.lease_id, payment.amount, description);
  }

  payLink(payment: Payment): string {
    return `https://pay.tidy.com/r/${payment.id}`;
  }

  estimateFee(amount: number, methodKey: PaymentMethodKey): number {
    switch (methodKey) {
      case 'ach': return Math.min(amount * 0.008, 5);
      case 'card': return amount * 0.029 + 0.30;
      case 'wire': return 15;
      default: return 0;
    }
  }

  methodLabel(key: PaymentMethodKey): PaymentMethodLabel {
    switch (key) {
      case 'ach': return 'ACH';
      case 'card': return 'Credit Card';
      case 'wire': return 'Wire';
    }
  }

  private newId(): string {
    try {
      const c: any = (globalThis as any).crypto;
      if (c?.randomUUID) { return c.randomUUID(); }
    } catch { /* no crypto */ }
    return `pay-${new Date().getTime()}`;
  }

  // ---- Payment methods config ----
  async getPaymentMethods(): Promise<PaymentMethodOption[]> {
    if (this.useDummyData) { return PAYMENT_METHODS; }
    return firstValueFrom(this.http.get<PaymentMethodOption[]>(`${environment.api_url}api/v1/customer/payment-methods-config`));
  }

  async setPaymentMethodEnabled(key: PaymentMethodKey, enabled: boolean): Promise<void> {
    const method = PAYMENT_METHODS.find(m => m.key === key);
    if (method) { method.enabled = enabled; }
  }

  async setPaymentMethodFeePayer(key: PaymentMethodKey, payer: 'tenant' | 'owner'): Promise<void> {
    const method = PAYMENT_METHODS.find(m => m.key === key);
    if (method) { method.fee_paid_by = payer; }
  }

  // ---- Payout account ----
  async getPayoutAccount(): Promise<PayoutAccount> {
    if (this.useDummyData) { return PAYOUT_ACCOUNT; }
    return firstValueFrom(this.http.get<PayoutAccount>(`${environment.api_url}api/v1/customer/payout-account`));
  }

  async connectPayout(): Promise<PayoutAccount> {
    if (this.useDummyData) {
      PAYOUT_ACCOUNT.connected = true;
      return PAYOUT_ACCOUNT;
    }
    return firstValueFrom(this.http.post<PayoutAccount>(`${environment.api_url}api/v1/customer/payout-account`, {}));
  }

  async setPayoutSchedule(schedule: PayoutAccount['schedule']): Promise<void> {
    PAYOUT_ACCOUNT.schedule = schedule;
  }

  // ---- Reminders ----
  async getReminderSettings(): Promise<ReminderSettings> {
    if (this.useDummyData) { return REMINDER_SETTINGS; }
    return firstValueFrom(this.http.get<ReminderSettings>(`${environment.api_url}api/v1/customer/payment-reminders`));
  }

  async updateReminderSettings(settings: Partial<ReminderSettings>): Promise<ReminderSettings> {
    Object.assign(REMINDER_SETTINGS, settings);
    return REMINDER_SETTINGS;
  }

  async toggleReminderRule(id: string, enabled: boolean): Promise<void> {
    const rule = REMINDER_SETTINGS.rules.find(r => r.id === id);
    if (rule) { rule.enabled = enabled; }
  }

  async updateReminderRuleMessage(id: string, message: string): Promise<void> {
    const rule = REMINDER_SETTINGS.rules.find(r => r.id === id);
    if (rule) { rule.message = message; }
  }
}
